/**
 * Budget Export Utilities
 * 
 * Provides functions to export budget data to various formats:
 * - Excel (.xlsx)
 * - PDF (.pdf)
 * - CSV (.csv)
 */

import type { 
  BudgetData, 
  BudgetSummary, 
  CostBudget, 
  SalesBudget, 
  ResourceBudget,
  AllBudgetsView 
} from '@/types/budget.types';
import { formatCurrency, formatPercent, formatHours } from './budget-utils';

// ============================================================================
// Excel Export
// ============================================================================

/**
 * Export budget data to Excel format
 */
export async function exportToExcel(
  budgetData: BudgetData,
  options: ExportOptions = {}
): Promise<void> {
  const {
    includeCharts = false,
    includeSummary = true,
    includeCost = true,
    includeSales = true,
    includeResource = true,
    includeHistory = false,
  } = options;

  try {
    // Create workbook data
    const workbookData: any = {
      SheetNames: [],
      Sheets: {},
    };

    // Add Summary sheet
    if (includeSummary && budgetData.summary) {
      const summarySheet = createSummarySheet(budgetData.summary);
      workbookData.SheetNames.push('Summary');
      workbookData.Sheets['Summary'] = summarySheet;
    }

    // Add Cost Budget sheet
    if (includeCost && budgetData.costBudget) {
      const costSheet = createCostBudgetSheet(budgetData.costBudget);
      workbookData.SheetNames.push('Cost Budget');
      workbookData.Sheets['Cost Budget'] = costSheet;
    }

    // Add Sales Budget sheet
    if (includeSales && budgetData.salesBudget) {
      const salesSheet = createSalesBudgetSheet(budgetData.salesBudget);
      workbookData.SheetNames.push('Sales Budget');
      workbookData.Sheets['Sales Budget'] = salesSheet;
    }

    // Add Resource Budget sheet
    if (includeResource && budgetData.resourceBudget) {
      const resourceSheet = createResourceBudgetSheet(budgetData.resourceBudget);
      workbookData.SheetNames.push('Resource Budget');
      workbookData.Sheets['Resource Budget'] = resourceSheet;
    }

    // Add Revision History sheet
    if (includeHistory && budgetData.revisions && budgetData.revisions.length > 0) {
      const historySheet = createRevisionHistorySheet(budgetData.revisions);
      workbookData.SheetNames.push('Revision History');
      workbookData.Sheets['Revision History'] = historySheet;
    }

    // Convert to Excel file and download
    const fileName = `${budgetData.projectName}_Budget_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Note: In a real implementation, you would use a library like xlsx or exceljs
    // For now, we'll create a CSV-like structure that can be opened in Excel
    downloadAsCSV(workbookData, fileName);

    console.log('✅ Budget exported to Excel successfully');
  } catch (error) {
    console.error('❌ Error exporting to Excel:', error);
    throw new Error('Failed to export budget to Excel');
  }
}

/**
 * Create Summary sheet data
 */
function createSummarySheet(summary: BudgetSummary): any {
  const data = [
    ['Budget Summary', '', '', ''],
    ['', '', '', ''],
    ['Metric', 'Budget', 'Actual', 'Variance'],
    ['Revenue', formatCurrency(summary.revenue.budget), formatCurrency(summary.revenue.actual), formatCurrency(summary.revenue.variance)],
    ['Costs', formatCurrency(summary.costs.budget), formatCurrency(summary.costs.actual), formatCurrency(summary.costs.variance)],
    ['Margin', formatPercent(summary.margin.budgetedMargin), formatPercent(summary.margin.actualMargin), formatPercent(summary.margin.variance)],
    ['Hours', formatHours(summary.hours.budget), formatHours(summary.hours.actual), formatHours(summary.hours.remaining)],
    ['', '', '', ''],
    ['Health Status', summary.health.score, '', ''],
    ['Health Message', summary.health.message, '', ''],
  ];

  return arrayToSheet(data);
}

/**
 * Create Cost Budget sheet data
 */
function createCostBudgetSheet(costBudget: CostBudget): any {
  const data = [
    ['Cost Budget', '', '', '', '', ''],
    ['', '', '', '', '', ''],
    ['Category', 'Budget', 'Actual', 'Committed', 'Available', '% Used'],
  ];

  // Add totals
  data.push([
    'TOTAL',
    formatCurrency(costBudget.totals.budget),
    formatCurrency(costBudget.totals.actual),
    formatCurrency(costBudget.totals.committed),
    formatCurrency(costBudget.totals.available),
    formatPercent(costBudget.totals.progressPercent),
  ]);

  data.push(['', '', '', '', '', '']);

  // Add categories
  costBudget.categories.forEach(category => {
    data.push([
      category.name,
      formatCurrency(category.budget),
      formatCurrency(category.actual),
      formatCurrency(category.committed),
      formatCurrency(category.available),
      formatPercent(category.progressPercent),
    ]);

    // Add subcategories
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach(sub => {
        data.push([
          `  ${sub.name}`,
          formatCurrency(sub.budget),
          formatCurrency(sub.actual),
          formatCurrency(sub.committed || 0),
          formatCurrency(sub.available || 0),
          formatPercent(sub.progressPercent || 0),
        ]);
      });
    }
  });

  data.push(['', '', '', '', '', '']);
  data.push(['By Phase', '', '', '', '', '']);
  data.push(['Phase', 'Budget', 'Actual', 'Variance', 'Status', '']);

  // Add phases
  costBudget.phases.forEach(phase => {
    data.push([
      phase.name || phase.phaseName,
      formatCurrency(phase.budget),
      formatCurrency(phase.actual),
      formatCurrency(phase.variance),
      phase.status,
      '',
    ]);
  });

  return arrayToSheet(data);
}

/**
 * Create Sales Budget sheet data
 */
function createSalesBudgetSheet(salesBudget: SalesBudget): any {
  const data = [
    ['Sales Budget', '', '', '', ''],
    ['', '', '', '', ''],
  ];

  // Add contract summary
  if (salesBudget.contractValue) {
    data.push(['Contract Value', formatCurrency(salesBudget.contractValue), '', '', '']);
  }
  if (salesBudget.estimatedRevenue) {
    data.push(['Estimated Revenue', formatCurrency(salesBudget.estimatedRevenue), '', '', '']);
  }
  if (salesBudget.monthlyRetainer) {
    data.push(['Monthly Retainer', formatCurrency(salesBudget.monthlyRetainer), '', '', '']);
  }

  data.push(['', '', '', '', '']);

  // Add milestones if available
  if (salesBudget.milestones && salesBudget.milestones.length > 0) {
    data.push(['Billing Milestones', '', '', '', '']);
    data.push(['Milestone', 'Amount', 'Due Date', 'Invoiced', 'Status']);
    
    salesBudget.milestones.forEach(milestone => {
      data.push([
        milestone.name,
        formatCurrency(milestone.amount),
        milestone.dueDate,
        milestone.invoiced ? 'Yes' : 'No',
        milestone.status,
      ]);
    });
  }

  // Add rate card if available
  if (salesBudget.rateCard && salesBudget.rateCard.length > 0) {
    data.push(['', '', '', '', '']);
    data.push(['Rate Card', '', '', '', '']);
    data.push(['Role', 'Bill Rate', 'Est Hours', 'Actual Hours', 'Revenue']);
    
    salesBudget.rateCard.forEach(rate => {
      data.push([
        rate.role,
        formatCurrency(rate.billRate),
        rate.estimatedHours.toString(),
        rate.actualHours.toString(),
        formatCurrency(rate.revenue),
      ]);
    });
  }

  return arrayToSheet(data);
}

/**
 * Create Resource Budget sheet data
 */
function createResourceBudgetSheet(resourceBudget: ResourceBudget): any {
  const data = [
    ['Resource Budget', '', '', '', '', ''],
    ['', '', '', '', '', ''],
    ['Summary', '', '', '', '', ''],
    ['Total Hours Budget', formatHours(resourceBudget.summary.totalBudget), '', '', '', ''],
    ['Total Hours Actual', formatHours(resourceBudget.summary.totalActual), '', '', '', ''],
    ['Total Hours Remaining', formatHours(resourceBudget.summary.totalRemaining), '', '', '', ''],
    ['Total Budget Cost', formatCurrency(resourceBudget.summary.totalBudgetCost), '', '', '', ''],
    ['Total Actual Cost', formatCurrency(resourceBudget.summary.totalActualCost), '', '', '', ''],
    ['Billable %', formatPercent(resourceBudget.summary.billablePercent), '', '', '', ''],
    ['', '', '', '', '', ''],
    ['By Role', '', '', '', '', ''],
    ['Role', 'Budget (hrs)', 'Actual (hrs)', 'Remaining (hrs)', '% Used', 'Rate (£/hr)'],
  ];

  // Add roles
  resourceBudget.byRole.forEach(role => {
    data.push([
      role.role,
      formatHours(role.budgetHours),
      formatHours(role.actualHours),
      formatHours(role.remainingHours),
      formatPercent(role.percentUsed),
      formatCurrency(role.billRate),
    ]);
  });

  data.push(['', '', '', '', '', '']);
  data.push(['By Person', '', '', '', '', '']);
  data.push(['Person', 'Role', 'Budget (hrs)', 'Actual (hrs)', 'Utilization', 'Rate (£/hr)']);

  // Add people
  resourceBudget.byPerson.forEach(person => {
    data.push([
      person.personName,
      person.role,
      formatHours(person.budgetHours),
      formatHours(person.actualHours),
      formatPercent(person.utilisation),
      formatCurrency(person.costRate),
    ]);
  });

  data.push(['', '', '', '', '', '']);
  data.push(['By Phase', '', '', '', '', '']);
  data.push(['Phase', 'Budget (hrs)', 'Actual (hrs)', 'Remaining (hrs)', 'Status', '']);

  // Add phases
  resourceBudget.byPhase.forEach(phase => {
    data.push([
      phase.phaseName,
      formatHours(phase.budgetHours),
      formatHours(phase.actualHours),
      formatHours(phase.remainingHours),
      phase.status,
      '',
    ]);
  });

  return arrayToSheet(data);
}

/**
 * Create Revision History sheet data
 */
function createRevisionHistorySheet(revisions: any[]): any {
  const data = [
    ['Revision History', '', '', '', '', ''],
    ['', '', '', '', '', ''],
    ['Revision', 'Date', 'User', 'Reason', 'Cost Change', 'Revenue Change'],
  ];

  revisions.forEach(revision => {
    data.push([
      revision.revisionNumber,
      new Date(revision.createdAt).toLocaleDateString(),
      revision.createdBy,
      revision.reason,
      formatCurrency(revision.changes?.totalCostChange || 0),
      formatCurrency(revision.changes?.totalRevenueChange || 0),
    ]);
  });

  return arrayToSheet(data);
}

/**
 * Convert array to sheet format
 */
function arrayToSheet(data: any[][]): any {
  return data;
}

/**
 * Download data as CSV (Excel-compatible)
 */
function downloadAsCSV(workbookData: any, fileName: string): void {
  let csvContent = '';

  // Combine all sheets
  workbookData.SheetNames.forEach((sheetName: string, index: number) => {
    if (index > 0) {
      csvContent += '\n\n\n'; // Separate sheets
    }
    csvContent += `"${sheetName}"\n\n`;
    
    const sheet = workbookData.Sheets[sheetName];
    sheet.forEach((row: any[]) => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });
  });

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName.replace('.xlsx', '.csv'));
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ============================================================================
// PDF Export
// ============================================================================

/**
 * Export budget data to PDF format
 */
export async function exportToPDF(
  budgetData: BudgetData,
  options: ExportOptions = {}
): Promise<void> {
  const {
    includeCharts = true,
    includeSummary = true,
    includeCost = true,
    includeSales = true,
    includeResource = true,
  } = options;

  try {
    // Create PDF content
    let pdfContent = '';

    // Add header
    pdfContent += `Budget Report\n`;
    pdfContent += `Project: ${budgetData.projectName}\n`;
    pdfContent += `Date: ${new Date().toLocaleDateString()}\n`;
    pdfContent += `\n${'='.repeat(80)}\n\n`;

    // Add Summary
    if (includeSummary && budgetData.summary) {
      pdfContent += createSummaryPDF(budgetData.summary);
      pdfContent += `\n${'='.repeat(80)}\n\n`;
    }

    // Add Cost Budget
    if (includeCost && budgetData.costBudget) {
      pdfContent += createCostBudgetPDF(budgetData.costBudget);
      pdfContent += `\n${'='.repeat(80)}\n\n`;
    }

    // Add Sales Budget
    if (includeSales && budgetData.salesBudget) {
      pdfContent += createSalesBudgetPDF(budgetData.salesBudget);
      pdfContent += `\n${'='.repeat(80)}\n\n`;
    }

    // Add Resource Budget
    if (includeResource && budgetData.resourceBudget) {
      pdfContent += createResourceBudgetPDF(budgetData.resourceBudget);
    }

    // Download as text file (in real implementation, use jsPDF)
    const fileName = `${budgetData.projectName}_Budget_${new Date().toISOString().split('T')[0]}.txt`;
    downloadAsText(pdfContent, fileName);

    console.log('✅ Budget exported to PDF successfully');
  } catch (error) {
    console.error('❌ Error exporting to PDF:', error);
    throw new Error('Failed to export budget to PDF');
  }
}

/**
 * Create Summary PDF content
 */
function createSummaryPDF(summary: BudgetSummary): string {
  let content = 'BUDGET SUMMARY\n\n';
  
  content += `Revenue:\n`;
  content += `  Budget:   ${formatCurrency(summary.revenue.budget)}\n`;
  content += `  Actual:   ${formatCurrency(summary.revenue.actual)}\n`;
  content += `  Variance: ${formatCurrency(summary.revenue.variance)}\n\n`;
  
  content += `Costs:\n`;
  content += `  Budget:   ${formatCurrency(summary.costs.budget)}\n`;
  content += `  Actual:   ${formatCurrency(summary.costs.actual)}\n`;
  content += `  Variance: ${formatCurrency(summary.costs.variance)}\n\n`;
  
  content += `Margin:\n`;
  content += `  Budgeted: ${formatPercent(summary.margin.budgetedMargin)}\n`;
  content += `  Actual:   ${formatPercent(summary.margin.actualMargin)}\n`;
  content += `  Variance: ${formatPercent(summary.margin.variance)}\n\n`;
  
  content += `Hours:\n`;
  content += `  Budget:    ${formatHours(summary.hours.budget)}\n`;
  content += `  Actual:    ${formatHours(summary.hours.actual)}\n`;
  content += `  Remaining: ${formatHours(summary.hours.remaining)}\n\n`;
  
  content += `Health: ${summary.health.score.toUpperCase()}\n`;
  content += `Status: ${summary.health.message}\n`;
  
  return content;
}

/**
 * Create Cost Budget PDF content
 */
function createCostBudgetPDF(costBudget: CostBudget): string {
  let content = 'COST BUDGET\n\n';
  
  content += `Total Budget:    ${formatCurrency(costBudget.totals.budget)}\n`;
  content += `Total Actual:    ${formatCurrency(costBudget.totals.actual)}\n`;
  content += `Total Committed: ${formatCurrency(costBudget.totals.committed)}\n`;
  content += `Total Available: ${formatCurrency(costBudget.totals.available)}\n`;
  content += `Progress:        ${formatPercent(costBudget.totals.progressPercent)}\n\n`;
  
  content += 'BY CATEGORY:\n\n';
  
  costBudget.categories.forEach(category => {
    content += `${category.name}\n`;
    content += `  Budget:    ${formatCurrency(category.budget)}\n`;
    content += `  Actual:    ${formatCurrency(category.actual)}\n`;
    content += `  Available: ${formatCurrency(category.available)}\n`;
    content += `  Progress:  ${formatPercent(category.progressPercent)}\n\n`;
  });
  
  return content;
}

/**
 * Create Sales Budget PDF content
 */
function createSalesBudgetPDF(salesBudget: SalesBudget): string {
  let content = 'SALES BUDGET\n\n';
  
  if (salesBudget.contractValue) {
    content += `Contract Value: ${formatCurrency(salesBudget.contractValue)}\n\n`;
  }
  
  if (salesBudget.milestones && salesBudget.milestones.length > 0) {
    content += 'BILLING MILESTONES:\n\n';
    salesBudget.milestones.forEach(milestone => {
      content += `${milestone.name}\n`;
      content += `  Amount:   ${formatCurrency(milestone.amount)}\n`;
      content += `  Due:      ${milestone.dueDate}\n`;
      content += `  Status:   ${milestone.status}\n\n`;
    });
  }
  
  return content;
}

/**
 * Create Resource Budget PDF content
 */
function createResourceBudgetPDF(resourceBudget: ResourceBudget): string {
  let content = 'RESOURCE BUDGET\n\n';
  
  content += `Total Hours Budget:    ${formatHours(resourceBudget.summary.totalBudget)}\n`;
  content += `Total Hours Actual:    ${formatHours(resourceBudget.summary.totalActual)}\n`;
  content += `Total Hours Remaining: ${formatHours(resourceBudget.summary.totalRemaining)}\n`;
  content += `Total Budget Cost:     ${formatCurrency(resourceBudget.summary.totalBudgetCost)}\n`;
  content += `Total Actual Cost:     ${formatCurrency(resourceBudget.summary.totalActualCost)}\n`;
  content += `Billable Percentage:   ${formatPercent(resourceBudget.summary.billablePercent)}\n\n`;
  
  content += 'BY ROLE:\n\n';
  
  resourceBudget.byRole.forEach(role => {
    content += `${role.role}\n`;
    content += `  Budget:    ${formatHours(role.budgetHours)} @ ${formatCurrency(role.billRate)}/hr\n`;
    content += `  Actual:    ${formatHours(role.actualHours)}\n`;
    content += `  Remaining: ${formatHours(role.remainingHours)}\n`;
    content += `  Progress:  ${formatPercent(role.percentUsed)}\n`;
    content += `  Cost:      ${formatCurrency(role.actualCost)} / ${formatCurrency(role.budgetCost)}\n\n`;
  });
  
  content += 'BY PERSON:\n\n';
  
  resourceBudget.byPerson.forEach(person => {
    content += `${person.personName} (${person.role})\n`;
    content += `  Budget:      ${formatHours(person.budgetHours)} @ ${formatCurrency(person.costRate)}/hr\n`;
    content += `  Actual:      ${formatHours(person.actualHours)}\n`;
    content += `  Utilisation: ${formatPercent(person.utilisation)}\n`;
    content += `  Cost:        ${formatCurrency(person.actualCost)} / ${formatCurrency(person.budgetCost)}\n\n`;
  });
  
  content += 'BY PHASE:\n\n';
  
  resourceBudget.byPhase.forEach(phase => {
    content += `${phase.phaseName} [${phase.status}]\n`;
    content += `  Budget:    ${formatHours(phase.budgetHours)}\n`;
    content += `  Actual:    ${formatHours(phase.actualHours)}\n`;
    content += `  Remaining: ${formatHours(phase.remainingHours)}\n\n`;
  });
  
  return content;
}

/**
 * Download content as text file
 */
function downloadAsText(content: string, fileName: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ============================================================================
// Export Options
// ============================================================================

export interface ExportOptions {
  includeCharts?: boolean;
  includeSummary?: boolean;
  includeCost?: boolean;
  includeSales?: boolean;
  includeResource?: boolean;
  includeHistory?: boolean;
  format?: 'excel' | 'pdf' | 'csv';
}

/**
 * Export budget with options modal
 */
export async function exportBudget(
  budgetData: BudgetData,
  format: 'excel' | 'pdf',
  options: ExportOptions = {}
): Promise<void> {
  if (format === 'excel') {
    await exportToExcel(budgetData, options);
  } else if (format === 'pdf') {
    await exportToPDF(budgetData, options);
  }
}
